import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageSeo } from "@/hooks/usePageSeo";
import FilterPanel from "@/pages/category/components/FilterPanel";
import VideoResultList from "@/pages/category/components/VideoResultList";
import {
  applyFilters,
  getCategoryBySlug,
  getVideosByCategory,
  paginate,
  sortVideos,
  type Filters,
  type SortKey,
} from "@/lib/videos";

const PAGE_SIZE = 9;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const slug = type || "movie";
  const category = getCategoryBySlug(slug);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({ types: [], regions: [], years: [], durations: [] });

  const categoryName = category?.name || "视频分类";

  usePageSeo({
    title: `激情视频 - ${categoryName}视频分类精选`,
    description: category
      ? `${category.description} 激情视频${categoryName}分类共收录多部精选作品，支持按地区、年份、时长筛选与排序。`
      : "激情视频视频分类页，按题材、地区、年份筛选精选激情影视与短视频作品。",
    keywords: `激情视频,${categoryName},激情影视分类,短视频分类,高清视频`,
  });

  const list = useMemo(() => getVideosByCategory(slug), [slug]);
  const filtered = useMemo(() => sortVideos(applyFilters(list, filters), sort), [list, filters, sort]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const items = paginate(filtered, currentPage, PAGE_SIZE);

  const updateFilter = (key: keyof Filters, values: string[]) => {
    setFilters((prev) => ({ ...prev, [key]: values }));
    setPage(1);
  };

  const resetAll = () => {
    setFilters({ types: [], regions: [], years: [], durations: [] });
    setSort("latest");
    setPage(1);
  };

  const panel = (
    <FilterPanel
      filters={filters}
      onTypesChange={(values) => updateFilter("types", values)}
      onRegionsChange={(values) => updateFilter("regions", values)}
      onYearsChange={(values) => updateFilter("years", values)}
      onDurationsChange={(values) => updateFilter("durations", values)}
      onReset={resetAll}
    />
  );

  return (
    <SiteLayout>
      <div className="px-4 md:px-8 lg:px-12 pt-5 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "视频分类", to: "/category/movie" }, { label: categoryName }]} />
      </div>

      <section className="px-4 md:px-8 lg:px-12 mt-4">
        <div className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-md bg-primary-500/15 text-primary-400 flex items-center justify-center shrink-0">
            <i className={`${category?.icon || "ri-film-line"} text-2xl`} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-50">{categoryName}</h1>
            <p className="mt-2 text-sm text-foreground-400 max-w-2xl leading-relaxed">
              {category?.description || "按类型、地区、年份与时长筛选，快速找到感兴趣的精选作品。"}
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div>
              <p className="font-heading text-2xl font-bold text-foreground-50">{list.length}</p>
              <p className="text-[11px] text-foreground-500">收录作品</p>
            </div>
            <div className="w-px h-9 bg-background-300/60" />
            <div>
              <p className="font-heading text-2xl font-bold text-accent-400">{filtered.length}</p>
              <p className="text-[11px] text-foreground-500">筛选结果</p>
            </div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 lg:px-12 mt-6 md:mt-8">
        <div className="flex flex-col lg:flex-row gap-6">
          <aside className="hidden lg:block w-[248px] shrink-0">{panel}</aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 text-xs text-foreground-500 overflow-x-auto no-scrollbar">
                <i className="ri-map-pin-line text-foreground-600" />
                当前分类：<span className="text-foreground-200 whitespace-nowrap">{categoryName}</span>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="lg:hidden flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-300/70 text-xs text-foreground-200 whitespace-nowrap cursor-pointer shrink-0"
              >
                <i className="ri-filter-3-line" />
                筛选
              </button>
            </div>

            <VideoResultList
              videos={items}
              sort={sort}
              onSortChange={(value) => {
                setSort(value);
                setPage(1);
              }}
              page={currentPage}
              totalPages={totalPages}
              onPageChange={(next) => {
                setPage(next);
                window.scrollTo({ top: 240, behavior: "smooth" });
              }}
              emptyTitle="这个筛选组合下暂时没有作品"
              emptyDescription="试着减少筛选条件，或把年份、时长放宽一点，我们会继续补充新的内容。"
              onReset={resetAll}
              emptyActionTo="/category/theme"
              emptyActionLabel="看看热门主题"
              resultCount={filtered.length}
            />
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 mt-10">
        <div className="rounded-lg bg-background-200/40 border border-background-200/70 p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-sm font-medium text-foreground-100">还没找到想看的？</h2>
            <p className="mt-1 text-xs text-foreground-500">用关键词全站搜索，或直接查看热门主题片单。</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/search"
              className="h-9 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer"
            >
              <i className="ri-search-line" />
              全站搜索
            </Link>
            <Link
              to="/category/theme"
              className="h-9 px-4 rounded-md border border-background-300/70 text-xs text-foreground-200 hover:border-primary-400 transition-colors whitespace-nowrap flex items-center cursor-pointer"
            >
              热门主题
            </Link>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-background-950/70 cursor-pointer"
          />
          <div className="absolute right-0 top-0 bottom-0 w-[86%] max-w-[340px] bg-background-100 overflow-y-auto scrollbar-thin animate-slide-in-right">
            <div className="flex items-center justify-between px-4 h-14 border-b border-background-200/70 sticky top-0 bg-background-100 z-10">
              <span className="text-sm font-medium text-foreground-50">筛选条件</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-foreground-300 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-4">
              {panel}
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="mt-5 w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
              >
                查看 {filtered.length} 个结果
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}